import React, { useEffect, useId, useRef } from 'react'
import LandingHeader from '../../components/LandingHeader/LandingHeader'
import LandingFooter from '../../components/LandingFooter/LandingFooter'
import Seo from '../../components/Seo/Seo'
import './FormPage.css'

/* Shared palette / type — matches Contact.js */
export const MAROON = '#8A2233'
export const INK = '#1A1917'
export const DARK = '#17161A'
export const CREAM = '#FAF9F7'
export const EXP = "'Archivo Expanded','Archivo',sans-serif"

export const eyebrow = (color = MAROON) => ({
  fontSize: '12.5px', letterSpacing: '.18em', textTransform: 'uppercase',
  color, fontWeight: 700, margin: 0,
})
export const cardEyebrow = {
  fontSize: '12px', letterSpacing: '.16em', textTransform: 'uppercase',
  color: MAROON, fontWeight: 700, marginBottom: '18px',
}
export const labelStyle = {
  display: 'block', fontSize: '13px', color: '#4A4744', marginBottom: '7px', fontWeight: 600,
}
export const fieldStyle = {
  width: '100%', padding: '13px 15px', background: '#FAF9F7', border: '1px solid #E4DFD7',
  borderRadius: '9px', color: '#1A1917', fontFamily: "'Archivo',sans-serif", fontSize: '15px',
  transition: 'border-color .2s, box-shadow .2s',
}
export const cardStyle = {
  background: '#fff', border: '1px solid #E9E4DC', borderRadius: '18px',
  padding: 'clamp(24px,3vw,40px)', boxShadow: '0 1px 2px rgba(26,25,23,.04)',
}
export const helpText = { fontSize: '13px', color: '#8A857F', lineHeight: 1.55, margin: 0 }

/**
 * Shared chrome for the public form pages: fixed nav, hero with a Download PDF
 * button, footer, and the scroll-reveal behaviour used elsewhere on the site.
 */
export default function FormLayout({ eyebrowLabel = 'For Veterinarians', title, subtitle, pdfHref, pdfLabel = 'Download PDF', children }) {
  const fallbackRef = useRef(null)

  useEffect(() => {
    const els = document.querySelectorAll('.q-reveal')
    if (!('IntersectionObserver' in window)) {
      els.forEach((e) => e.classList.add('q-in'))
      return
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('q-in')
            io.unobserve(en.target)
          }
        }),
      { threshold: 0.08, rootMargin: '0px 0px -6% 0px' },
    )
    els.forEach((e) => io.observe(e))
    fallbackRef.current = setTimeout(() => els.forEach((e) => e.classList.add('q-in')), 1600)
    return () => {
      io.disconnect()
      clearTimeout(fallbackRef.current)
    }
  }, [])

  return (
    <div
      style={{
        background: CREAM, color: INK, overflowX: 'clip',
        fontFamily: "'Archivo',sans-serif", minHeight: '100vh',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <Seo title={title ? `${title} | Qentoros` : 'Qentoros'} noindex />
      {/* Shared site header/navigation */}
      <LandingHeader />

      {/* Grows to fill any viewport taller than the content so the footer
          stays pinned to the bottom. */}
      <main id="main" tabIndex={-1} style={{ flex: '1 0 auto' }}>
      {/* HERO */}
      <section style={{ padding: 'clamp(96px,11vw,128px) 7vw clamp(24px,3vw,40px)', background: CREAM }}>
        <div
          className="q-reveal q-hero-row"
          style={{
            maxWidth: '1180px', margin: '0 auto', display: 'flex',
            alignItems: 'flex-end', justifyContent: 'space-between', gap: '28px', flexWrap: 'wrap',
          }}
        >
          <div style={{ maxWidth: '640px' }}>
            <p style={{ ...eyebrow(MAROON), marginBottom: '18px' }}>{eyebrowLabel}</p>
            <h1
              style={{
                fontFamily: EXP, fontWeight: 700, fontSize: 'clamp(34px,5vw,64px)',
                lineHeight: 1.02, letterSpacing: '-.025em', color: INK, marginBottom: '16px',
              }}
            >
              {title}
            </h1>
            {subtitle && (
              <p style={{ fontSize: 'clamp(16px,1.5vw,19px)', lineHeight: 1.55, color: '#5C5853', margin: 0 }}>
                {subtitle}
              </p>
            )}
          </div>
          {pdfHref && (
            <a
              href={pdfHref}
              target="_blank"
              rel="noopener"
              className="q-ghost"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px', flex: 'none',
                background: '#fff', color: '#4A4744', textDecoration: 'none', fontSize: '14.5px',
                fontWeight: 600, padding: '12px 20px', borderRadius: '10px',
                border: '1px solid #E4DFD7', transition: 'border-color .25s, color .25s',
              }}
            >
              <span aria-hidden="true">↓</span> {pdfLabel}
            </a>
          )}
        </div>
      </section>

      {/* FORM BODY — `id="form"` is the anchor the admin Leads page deep-links
          to (see src/utils/leadSources.js). */}
      <section id="form" style={{ padding: 'clamp(8px,1.5vw,16px) 7vw clamp(48px,6vw,76px)', background: CREAM }}>
        <div style={{ maxWidth: '820px', margin: '0 auto' }}>{children}</div>
      </section>
      </main>

      {/* Shared site footer */}
      <LandingFooter tagline="Harnessing the power of peptides to advance equine health." />
    </div>
  )
}

/**
 * Warm confirmation panel shown after a form is submitted. Self-animating (the
 * `q-success` class plays its own entrance) so it never depends on the
 * scroll-reveal observer, which only runs on mount and would otherwise leave
 * this post-submit panel invisible.
 *
 * @param {Object}   props
 * @param {string}   props.headline   Big reassuring headline.
 * @param {node}     props.intro      Supporting sentence(s) under the headline.
 * @param {node}    [props.summary]   Optional detail block (e.g. an order recap).
 * @param {Array}   [props.steps]     [{ title, body }] "what happens next" list.
 * @param {node}    [props.footer]    Optional CTA row at the bottom.
 * @param {string}  [props.accent]    Badge color (defaults to a confident green).
 * @param {string}  [props.badge]     Badge glyph (defaults to a checkmark); the
 *   same panel also reports states that aren't a success, e.g. an enrollment
 *   still awaiting review.
 */
export const SUCCESS_ACCENT = '#2E7D5B'

export function SuccessPanel({ headline, intro, summary, steps, footer, accent = SUCCESS_ACCENT, badge = '✓' }) {
  return (
    <div className="q-success" style={successCard}>
      {/* The glow and the arrival ring are tinted to the green by default; any
          other accent restyles both, so a maroon badge doesn't sit in a green
          halo. --q-badge-ring is read by .q-success-badge::after. */}
      <div
        className="q-success-badge"
        style={{
          ...successBadge,
          background: accent,
          ...(accent === SUCCESS_ACCENT
            ? null
            : {
                boxShadow: '0 8px 20px -6px rgba(26,25,23,.3)',
                '--q-badge-ring': `color-mix(in srgb, ${accent} 45%, transparent)`,
              }),
        }}
        aria-hidden="true"
      >
        {badge}
      </div>
      <h3 style={successHeadline}>{headline}</h3>
      {intro && <p style={successIntro}>{intro}</p>}

      {summary && <div style={successSummary}>{summary}</div>}

      {steps && steps.length > 0 && (
        <div style={{ textAlign: 'left', maxWidth: '460px', margin: '30px auto 0' }}>
          <p style={successStepsLabel}>What happens next</p>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '16px' }}>
            {steps.map((s, i) => (
              <li key={s.title} className="q-step" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                <span style={successStepNum}>{i + 1}</span>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: INK, marginBottom: '2px' }}>{s.title}</div>
                  <div style={{ fontSize: '14px', color: '#6E6A65', lineHeight: 1.5 }}>{s.body}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {footer && <div style={{ marginTop: '32px' }}>{footer}</div>}
    </div>
  )
}

const successCard = {
  position: 'relative',
  background: '#fff',
  border: '1px solid #E9E4DC',
  borderRadius: '20px',
  padding: 'clamp(32px,5vw,56px)',
  textAlign: 'center',
  boxShadow: '0 24px 60px -34px rgba(26,25,23,.34)',
}
const successBadge = {
  position: 'relative',
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  margin: '0 auto 22px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '29px',
  color: '#fff',
  boxShadow: '0 8px 20px -6px rgba(46,125,91,.5)',
}
const successHeadline = {
  fontFamily: EXP,
  fontWeight: 700,
  fontSize: 'clamp(23px,3vw,28px)',
  color: INK,
  marginBottom: '12px',
  letterSpacing: '-.01em',
  lineHeight: 1.1,
}
const successIntro = {
  fontSize: '16px',
  color: '#6E6A65',
  lineHeight: 1.6,
  maxWidth: '460px',
  margin: '0 auto',
}
const successSummary = {
  marginTop: '28px',
  padding: '18px 20px',
  background: '#FAF9F7',
  border: '1px solid #EFEAE2',
  borderRadius: '12px',
  textAlign: 'left',
  maxWidth: '460px',
  marginLeft: 'auto',
  marginRight: 'auto',
}
const successStepsLabel = {
  fontSize: '11.5px',
  letterSpacing: '.16em',
  textTransform: 'uppercase',
  color: MAROON,
  fontWeight: 700,
  margin: '0 0 16px',
}
const successStepNum = {
  flex: 'none',
  width: '26px',
  height: '26px',
  borderRadius: '50%',
  background: '#FBF4F4',
  border: `1px solid ${MAROON}`,
  color: MAROON,
  fontSize: '13px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '1px',
}

/* Section card with an uppercase maroon eyebrow, used by both form pages. */
export function Section({ label, children }) {
  return (
    <div className="q-reveal" style={cardStyle}>
      <p style={cardEyebrow}>{label}</p>
      {children}
    </div>
  )
}

/* Field with a label above it. The label is programmatically tied to the
   control it wraps: a generated id is placed on the label's htmlFor and
   cloned onto the child input/select/combobox (respecting an explicit id if
   the caller set one), so screen readers announce the visible label rather
   than falling back to the placeholder. */
export function Field({ label, optional, children }) {
  const generatedId = useId()
  const control = React.isValidElement(children) ? children : null
  const controlId = control?.props.id || generatedId
  return (
    <div>
      <label htmlFor={controlId} style={labelStyle}>
        {label}{optional && <span style={{ color: '#9A958F', fontWeight: 400 }}> (optional)</span>}
      </label>
      {control ? React.cloneElement(control, { id: controlId }) : children}
    </div>
  )
}

/* Selectable pill used for radio-style choices (Yes/No, delivery date). */
export function PillOption({ name, value, checked, onChange, children }) {
  return (
    <label
      className={`q-pill${checked ? ' q-pill--active' : ''}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '10px',
        padding: '12px 18px', background: '#FAF9F7', border: '1px solid #E4DFD7',
        borderRadius: '10px', fontSize: '15px', color: INK, transition: 'all .2s',
      }}
    >
      <input
        type="radio" name={name} value={value} checked={checked} onChange={onChange}
        style={{ accentColor: MAROON, width: '16px', height: '16px' }}
      />
      {children}
    </label>
  )
}
